import { useState, useRef } from 'react';
import Papa from 'papaparse';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Upload } from 'lucide-react';
import { logCreate, logAdminAction } from '@/lib/audit-log';

interface CSVUploaderProps {
  tableName: string;
  onComplete?: () => void;
  /** Column names used to detect duplicates in "smart insert" mode e.g. ['productname', 'upload_date'] */
  conflictKeys?: string[];
}

export function CSVUploader({ tableName, onComplete, conflictKeys }: CSVUploaderProps) {
  console.log("🚀 ~ CSVUploader ~ conflictKeys:", conflictKeys)
  const [data, setData] = useState<Record<string, any>[]>([]);
  console.log("🚀 ~ CSVUploader ~ data:", data)
  const [headers, setHeaders] = useState<string[]>([]);
  const [mode, setMode] = useState<'append' | 'replace' | 'smart'>('smart');
  const [loading, setLoading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        setHeaders(results.meta.fields || []);
        setData(results.data as Record<string, any>[]);
      },
    });
  };

  const handleCommit = async () => {
    if (!data.length) return;
    setLoading(true);
    try {
      // if (mode === 'replace') {
      //   const { data: oldData } = await supabase.from(tableName).select('*');
      //   const { error } = await supabase.from(tableName).delete().not('id', 'is', null);
      //   if (error) throw error;

      //   await logAdminAction({
      //     action: 'DELETE',
      //     tableName: tableName,
      //     oldValues: { count: oldData?.length || 0 },
      //     metadata: {
      //       description: `Deleted all ${oldData?.length || 0} rows from ${tableName} (CSV replace mode)`,
      //       mode: 'replace', operation: 'csv_upload' }
      //   });
      // }

      let rowsToInsert = data;
      let skipped = 0;

      if (mode === 'smart' && conflictKeys?.length) {
        // Normalize date columns to YYYY-MM-DD so formats like
        // "April 10, 2026" / "04/10/2026" / "2026-04-10" all compare equal
        const normalizeValue = (colName: string, value: string) => {
          if (colName.toLowerCase().includes('date') && value) {
            // ISO format already (YYYY-MM-DD) — extract directly, never touch Date()
            // so timezone can never shift the day
            const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
            if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;

            // Natural language format (e.g. "April 10, 2026") — parse then use
            // local date parts, not UTC, so the day stays correct in any timezone
            const parsed = new Date(value);
            if (!isNaN(parsed.getTime())) {
              const y = parsed.getFullYear();
              const m = String(parsed.getMonth() + 1).padStart(2, '0');
              const d = String(parsed.getDate()).padStart(2, '0');
              return `${y}-${m}-${d}`;
            }
          }
          return value;
        };

        const buildKey = (row: Record<string, any>) => {
          // console.log("🚀 ~ buildKey ~ row:", row)
          return conflictKeys.map((k) => normalizeValue(k, String(row[k] ?? ''))).join('|');
        };

        // Fetch only the conflict key columns from existing rows
        const { data: existing } = await supabase
          .from(tableName)
          .select(conflictKeys.join(','));

        // Build a Set of composite keys from existing data
        const existingKeys = new Set((existing || []).map(buildKey));
        // console.log("🚀 ~ handleCommit ~ existing Keys:", existingKeys)
        const newKeys = data.map(buildKey);
        // console.log("🚀 ~ handleCommit ~ new Keys:", newKeys)

        // Keep only rows whose composite key is NOT already in the DB
        rowsToInsert = data.filter((row) => !existingKeys.has(buildKey(row)));
        // console.log("🚀 ~ handleCommit ~ rowsToInsert:", rowsToInsert)

        skipped = data.length - rowsToInsert.length;
        console.log("🚀 ~ handleCommit ~ skipped:", skipped)
      }

      if (rowsToInsert.length > 0) {
        const sanitized = rowsToInsert.map((row) =>
        Object.fromEntries(
          Object.entries(row).map(([k, v]) => {
            const s = String(v ?? '').trim();
            // Null out unambiguous "no data" markers
            if (s === '' || s === '-') return [k, null];
            return [k, v];
          })
        )
      );
        const { error } = await supabase.from(tableName).insert(sanitized);
        if (error) throw error;
      }

      await logAdminAction({
        action: 'CREATE',
        tableName: tableName,
        newValues: { count: rowsToInsert.length, headers },
        metadata: {
          description: `Uploaded ${rowsToInsert.length} rows to ${tableName} via CSV (${mode} mode)`,
          mode,
          row_count: rowsToInsert.length,
          skipped,
          headers,
          operation: 'csv_upload'
        }
      });

      if (mode === 'smart') {
        toast.success(`${rowsToInsert.length} rows inserted, ${skipped} duplicates skipped`);
      } else {
        toast.success(`${rowsToInsert.length} rows ${mode === 'replace' ? 'replaced' : 'appended'} successfully`);
      }

      setData([]);
      setHeaders([]);
      if (fileRef.current) fileRef.current.value = '';
      onComplete?.();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <Button variant="outline" onClick={() => fileRef.current?.click()}>
          <Upload className="mr-2 h-4 w-4" /> Upload CSV
        </Button>
        <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleFile} />
      </div>
      {data.length > 0 && (
        <>
          <div className="rounded-md border max-h-64 overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {headers.map(h => <TableHead key={h}>{h}</TableHead>)}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.slice(0, 20).map((row, i) => (
                  <TableRow key={i}>
                    {headers.map(h => <TableCell key={h} className="max-w-[200px] truncate">{String(row[h] ?? '')}</TableCell>)}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {data.length > 20 && <p className="text-sm text-muted-foreground">Showing 20 of {data.length} rows</p>}
          <RadioGroup value={mode} onValueChange={(v) => setMode(v as 'append' | 'replace' | 'smart')} className="flex gap-6">
            <div className="flex items-center gap-2">
              <RadioGroupItem value="append" id="append" />
              <Label htmlFor="append">Append to existing</Label>
            </div>
            {/* <div className="flex items-center gap-2">
              <RadioGroupItem value="replace" id="replace" />
              <Label htmlFor="replace">Replace all</Label>
            </div> */}
            {conflictKeys?.length && (
              <div className="flex items-center gap-2">
                <RadioGroupItem value="smart" id="smart" />
                <Label htmlFor="smart">Smart insert (skip duplicates)</Label>
              </div>
            )}
          </RadioGroup>
          <Button onClick={handleCommit} disabled={loading}>
            {loading ? 'Committing...' : `Commit ${data.length} rows`}
          </Button>
        </>
      )}
    </div>
  );
}
